import dayjs from 'dayjs'
import { ExchangeTradingSession, TradingSession } from '../proto/generated/trading-calendar'
import {
    getTradingSessionsForSecurity,
    TradingSessionSecurityRef
} from './tradingSessionSelection'

/**
 * On a session calendar day, true while `atMs` is strictly before that session's regular open (`startTime`).
 * Used to disable intraday-style views until the regular session has opened.
 */
export function getBeforeRegularSessionOpenInfo(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    security: TradingSessionSecurityRef | undefined,
    atMs: number = Date.now()
): { beforeRegularOpen: boolean; session?: TradingSession } {
    const sessions = getTradingSessionsForSecurity(exchangeTradingSessions, security)
    if (!sessions.length) {
        return { beforeRegularOpen: false }
    }
    for (const ts of sessions) {
        if (!dayjs(ts.startTime).isSame(dayjs(atMs), 'day')) {
            continue
        }
        if (atMs < ts.startTime) {
            return { beforeRegularOpen: true, session: ts }
        }
    }
    return { beforeRegularOpen: false }
}

/** True during pre-open, regular, or post-close window for the symbol's exchange/segment/scope. */
export function isWithinChartLiveTradingHours(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    security: TradingSessionSecurityRef | undefined,
    atMs: number = Date.now()
): boolean {
    const sessions = getTradingSessionsForSecurity(exchangeTradingSessions, security)
    if (!sessions.length) return false

    for (const ts of sessions) {
        if (atMs >= ts.preOpenStartTime && atMs <= ts.preOpenEndTime) return true
        if (atMs >= ts.startTime && atMs <= ts.endTime) return true
        if (atMs >= ts.postCloseStartTime && atMs <= ts.postCloseEndTime) return true
    }
    return false
}

/** Epoch ms boundaries after `now` that can change live vs not-live for this security's sessions. */
export function getFutureTradingSessionBoundaries(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    security: TradingSessionSecurityRef | undefined,
    nowMs: number = Date.now()
): number[] {
    const sessions = getTradingSessionsForSecurity(exchangeTradingSessions, security)
    if (!sessions.length) return []

    const out: number[] = []
    for (const ts of sessions) {
        for (const t of [
            ts.preOpenStartTime,
            ts.preOpenEndTime,
            ts.startTime,
            ts.endTime,
            ts.postCloseStartTime,
            ts.postCloseEndTime
        ]) {
            if (t > nowMs) out.push(t)
        }
    }
    return out
}
